import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { ArrowLeft, Calendar } from 'lucide-react';
import BehaviorChart from '../../components/BehaviorChart';
import EatingLyingChart from '../../components/EatingLyingChart';
import ADGChart from '../../components/ADGChart';
import FCRChart from '../../components/FCRChart';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants';
import { storageService } from '../../services';
import { useGetHeatAlertsByCowQuery } from '@/store';

interface Cow {
  id: string;
  cowCode: string;
  name: string;
  sensorId: string;
  breed: string;
  gender: string;
  cattleType: string;
  farm: {
    id: string;
    name: string;
  };
}

const CowBehaviorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [cow, setCow] = useState<Cow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch heat alerts for this cow
  const { data: heatAlertsData } = useGetHeatAlertsByCowQuery(id!);

  // Date range state
  const [dateRange, setDateRange] = useState<'1d' | '3d' | '7d' | '14d' | '30d' | 'custom'>(
    '7d'
  );
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState<'activity' | 'eating-lying' | 'adg' | 'fcr'>('activity');

  useEffect(() => {
    if (id) {
      fetchCowData();
    }
  }, [id]);

  useEffect(() => {
    updateDateRange();
  }, [dateRange]);

  const fetchCowData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = storageService.getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.COWS.DETAIL(id!)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch cow data');
      }

      const data = await response.json();

      if (!data.sensorId) {
        throw new Error('โคนี้ยังไม่ได้ติดตั้ง sensor');
      }

      setCow(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const updateDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case '1d':
        setStartDate(subDays(now, 1));
        setEndDate(now);
        break;
      case '3d':
        setStartDate(subDays(now, 3));
        setEndDate(now);
        break;
      case '7d':
        setStartDate(subDays(now, 7));
        setEndDate(now);
        break;
      case '14d':
        setStartDate(subDays(now, 14));
        setEndDate(now);
        break;
      case '30d':
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
      case 'custom':
        // Keep custom dates
        break;
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setStartDate(new Date(customStartDate));
      setEndDate(new Date(customEndDate));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !cow) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'ไม่พบข้อมูลโค'}</p>
          <button
            onClick={() => navigate('/cows')}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            กลับไปหน้ารายการโค
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/cows/${id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          กลับไปหน้ารายละเอียดโค
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              กราฟพฤติกรรมโค
            </h1>
            <p className="text-gray-600 mt-1">
              {cow.cowCode} - {cow.name || 'ไม่มีชื่อ'} ({cow.breed})
            </p>
            <p className="text-sm text-gray-500 mt-1">
              ฟาร์ม: {cow.farm.name} | Sensor ID: {cow.sensorId}
            </p>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              ช่วงเวลา:
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setDateRange('1d')}
              className={`px-4 py-2 rounded text-sm font-medium transition ${
                dateRange === '1d'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              1 วัน
            </button>
            <button
              onClick={() => setDateRange('3d')}
              className={`px-4 py-2 rounded text-sm font-medium transition ${
                dateRange === '3d'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              3 วัน
            </button>
            <button
              onClick={() => setDateRange('7d')}
              className={`px-4 py-2 rounded text-sm font-medium transition ${
                dateRange === '7d'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              7 วัน
            </button>
            <button
              onClick={() => setDateRange('14d')}
              className={`px-4 py-2 rounded text-sm font-medium transition ${
                dateRange === '14d'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              14 วัน
            </button>
            <button
              onClick={() => setDateRange('30d')}
              className={`px-4 py-2 rounded text-sm font-medium transition ${
                dateRange === '30d'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              30 วัน
            </button>
            <button
              onClick={() => setDateRange('custom')}
              className={`px-4 py-2 rounded text-sm font-medium transition ${
                dateRange === 'custom'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              กำหนดเอง
            </button>
          </div>

          {dateRange === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
              <input
                type="datetime-local"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <span className="text-gray-500">ถึง</span>
              <input
                type="datetime-local"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
              >
                ใช้งาน
              </button>
            </div>
          )}

          <div className="text-sm text-gray-600 ml-auto">
            {format(startDate, 'dd/MM/yyyy HH:mm')} -{' '}
            {format(endDate, 'dd/MM/yyyy HH:mm')}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px flex-wrap">
            <button
              onClick={() => setActiveTab('activity')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'activity'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Activity Index
            </button>
            <button
              onClick={() => setActiveTab('eating-lying')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'eating-lying'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              พฤติกรรมการกินและนอน
            </button>
            <button
              onClick={() => setActiveTab('adg')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'adg'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ADG (อัตราการเจริญเติบโต)
            </button>
            <button
              onClick={() => setActiveTab('fcr')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'fcr'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              FCR (อัตราการแปลงอาหาร)
            </button>
          </nav>
        </div>
      </div>

      {/* Behavior Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'activity' && (
          <BehaviorChart
            cowId={cow.id}
            sensorId={cow.sensorId}
            startDate={startDate}
            endDate={endDate}
            heatAlerts={heatAlertsData?.data || []}
          />
        )}
        {activeTab === 'eating-lying' && (
          <EatingLyingChart
            cowId={cow.id}
            sensorId={cow.sensorId}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        {activeTab === 'adg' && (
          <ADGChart
            cowId={cow.id}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        {activeTab === 'fcr' && (
          <FCRChart
            cowId={cow.id}
            cowCattleType={cow.cattleType}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-600 mb-2">
            ช่วงเวลาที่เลือก
          </h3>
          <p className="text-2xl font-bold text-gray-900">
            {Math.ceil(
              (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
            )}{' '}
            วัน
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-600 mb-2">
            Sensor Status
          </h3>
          <p className="text-2xl font-bold text-green-600">Active</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-600 mb-2">
            ข้อมูลล่าสุด
          </h3>
          <p className="text-sm text-gray-900">
            {format(new Date(), 'dd/MM/yyyy HH:mm')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CowBehaviorPage;
